import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, SelectField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { api, errorMessage } from '../../api.js';
import { useI18n, type I18n } from '../../i18n.js';
import { dinarsToMinor } from '../shipping/CarriersPage.js';

type Order = Schemas['Order'];
type Cash = Schemas['OrderCash'];
type Entry = Cash['entries'][number];
type Carrier = Schemas['Carrier'];

function cashError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'validation-failed') return t('admin.cash.error.amount');
    if (failure.code === 'invalid-transition') return t('admin.cash.error.reverse');
  }
  return errorMessage(t, failure);
}

/**
 * Cash of a cash-on-delivery order: what was collected (by whom), refunded and still due,
 * with forms to record a collection or a refund and to reverse a mistaken entry.
 */
export function CashSection({
  storeId,
  order,
  canManage,
  onChange,
}: {
  readonly storeId: string;
  readonly order: Order;
  readonly canManage: boolean;
  readonly onChange: (order: Order) => Promise<void>;
}) {
  const { t, formatPrice, formatDateTime } = useI18n();
  const orderId = order.orderId;
  const [cash, setCash] = useState<Cash | null>(null);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [ledger, list] = await Promise.all([
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}/cash', {
          params: { path: { storeId, orderId } },
        }),
      ),
      unwrap(api.GET('/admin-api/v1/stores/{storeId}/carriers', { params: { path: { storeId } } })),
    ]);
    setCash(ledger);
    setCarriers(list.items);
  }, [storeId, orderId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t, order.version]);

  /** After a change: reload the order (payment status) and the ledger. */
  const changed = async (message: string) => {
    setNotice(message);
    const next = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}', {
        params: { path: { storeId, orderId } },
      }),
    );
    if (next.version === order.version) await load();
    else await onChange(next);
  };

  async function reverse(entry: Entry) {
    setNotice(null);
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/cash-entries/{entryId}/reverse', {
          params: { path: { storeId, entryId: entry.entryId } },
          body: { note: null },
        }),
      );
      await changed(t('admin.cash.reversedDone'));
    } catch (failure) {
      setError(cashError(t, failure));
    }
  }

  const describe = (entry: Entry) => {
    const parts = [
      t(`admin.cash.kind.${entry.kind}`),
      formatPrice(entry.amount.minor),
      ...(entry.kind === 'collection'
        ? [
            entry.carrierName === null
              ? t('admin.cash.byMerchant')
              : t('admin.cash.byCarrier', { carrier: entry.carrierName }),
          ]
        : []),
      ...(entry.shipmentNumber === null
        ? []
        : [t('admin.cash.parcel', { number: String(entry.shipmentNumber) })]),
      ...(entry.automatic ? [t('admin.cash.automatic')] : []),
      ...(entry.reversed ? [t('admin.cash.reversed')] : []),
      ...(entry.remitted ? [t('admin.cash.remitted')] : []),
    ];
    return parts.join(' — ');
  };

  return (
    <Card labelledBy="order-cash">
      <h2 id="order-cash" className="mrigl-subheading">
        {t('admin.cash.title')}
      </h2>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {cash === null ? null : (
        <>
          <p>{t('admin.cash.total', { amount: formatPrice(cash.total.minor) })}</p>
          <p>
            {t('admin.cash.collected', { amount: formatPrice(cash.collected.minor) })} —{' '}
            {t('admin.cash.refunded', { amount: formatPrice(cash.refunded.minor) })} —{' '}
            <strong>{t('admin.cash.due', { amount: formatPrice(cash.due.minor) })}</strong>
          </p>
          {cash.entries.length === 0 ? (
            <p className="mrigl-muted">{t('admin.cash.none')}</p>
          ) : (
            <ul className="mrigl-list">
              {cash.entries.map((entry) => (
                <li key={entry.entryId}>
                  <span className="mrigl-muted">{formatDateTime(entry.occurredAt)}</span>{' '}
                  {describe(entry)}
                  {entry.note === null ? null : (
                    <>
                      {' — '}
                      <span dir="auto">{entry.note}</span>
                    </>
                  )}{' '}
                  {canManage && entry.kind !== 'reversal' && !entry.reversed && !entry.remitted ? (
                    <Button variant="secondary" onClick={() => void reverse(entry)}>
                      {t('admin.cash.reverse', { date: formatDateTime(entry.occurredAt) })}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {canManage ? (
            <>
              {cash.due.minor === '0' ? null : (
                <EntryForm
                  kind="collection"
                  carriers={carriers}
                  onSubmit={async (amount, note, carrierId) => {
                    setNotice(null);
                    setError(null);
                    try {
                      await unwrap(
                        api.POST(
                          '/admin-api/v1/stores/{storeId}/orders/{orderId}/cash/collections',
                          {
                            params: { path: { storeId, orderId } },
                            body: {
                              amount: { minor: amount, currency: 'TND' },
                              carrierId: carrierId === '' ? null : carrierId,
                              note,
                            },
                          },
                        ),
                      );
                      await changed(t('admin.cash.collectedDone'));
                      return true;
                    } catch (failure) {
                      setError(cashError(t, failure));
                      return false;
                    }
                  }}
                />
              )}
              {cash.collected.minor === cash.refunded.minor ? null : (
                <EntryForm
                  kind="refund"
                  carriers={[]}
                  onSubmit={async (amount, note) => {
                    setNotice(null);
                    setError(null);
                    try {
                      await unwrap(
                        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/cash/refunds', {
                          params: { path: { storeId, orderId } },
                          body: { amount: { minor: amount, currency: 'TND' }, note },
                        }),
                      );
                      await changed(t('admin.cash.refundedDone'));
                      return true;
                    } catch (failure) {
                      setError(cashError(t, failure));
                      return false;
                    }
                  }}
                />
              )}
            </>
          ) : null}
        </>
      )}
    </Card>
  );
}

function EntryForm({
  kind,
  carriers,
  onSubmit,
}: {
  readonly kind: 'collection' | 'refund';
  readonly carriers: readonly Carrier[];
  readonly onSubmit: (amount: string, note: string | null, carrierId: string) => Promise<boolean>;
}) {
  const { t } = useI18n();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const label = kind === 'collection' ? t('admin.cash.collect') : t('admin.cash.refund');

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const minor = dinarsToMinor(amount);
    setInvalid(minor === null || minor === '0');
    if (minor === null || minor === '0') return;
    setBusy(true);
    const ok = await onSubmit(minor, note.trim() === '' ? null : note.trim(), carrierId);
    setBusy(false);
    if (ok) {
      setAmount('');
      setNote('');
    }
  }

  return (
    <form className="mrigl-stack" aria-label={label} onSubmit={(event) => void submit(event)}>
      <h3 className="mrigl-subheading">{label}</h3>
      <TextField
        label={t('admin.cash.amount')}
        inputMode="decimal"
        dir="ltr"
        required
        value={amount}
        error={invalid ? t('admin.cash.error.amount') : undefined}
        onChange={(event) => {
          setAmount(event.target.value);
        }}
      />
      {kind === 'collection' ? (
        <SelectField
          label={t('admin.cash.collector')}
          value={carrierId}
          onChange={(event) => {
            setCarrierId(event.target.value);
          }}
          options={[
            { value: '', label: t('admin.cash.merchant') },
            ...carriers.map((carrier) => ({ value: carrier.carrierId, label: carrier.name })),
          ]}
        />
      ) : null}
      <TextField
        label={t('admin.cash.note')}
        maxLength={300}
        dir="auto"
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      <Button type="submit" variant="secondary" busy={busy}>
        {label}
      </Button>
    </form>
  );
}
